"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Plus, Search } from "lucide-react";
import { apiGet, type Cliente, type Paginado, type ProductoResumen } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fecha } from "@/lib/fechas";
import { mensajeError } from "@/lib/errores";
import { ProductoDialog } from "@/components/producto-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/states";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";

export default function ProductosPage() {
  const t = useTranslations("productos");
  const tc = useTranslations("common");
  const { puedeEscribir } = useAuth();
  const router = useRouter();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [data, setData] = useState<Paginado<ProductoResumen> | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [nuevo, setNuevo] = useState(false);

  useEffect(() => {
    apiGet<Cliente[]>("/clientes").then(setClientes).catch(() => {});
  }, []);

  // Búsqueda con pequeño retardo para no lanzar una petición por tecla.
  useEffect(() => {
    const h = setTimeout(() => {
      setQDebounced(q);
      setPage(0);
    }, 300);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const p = new URLSearchParams({ page: String(page + 1), size: String(size) });
    if (qDebounced.trim()) p.set("q", qDebounced.trim());
    if (clienteId) p.set("cliente_id", clienteId);
    apiGet<Paginado<ProductoResumen>>(`/productos?${p}`)
      .then(setData)
      .catch((e) => toast.error(mensajeError(e)));
  }, [qDebounced, clienteId, page, size, toast]);

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
      <PageHeader
        title={t("titulo")}
        subtitle={t("subtitulo")}
        action={
          puedeEscribir && (
            <Button onClick={() => setNuevo(true)}>
              <Plus className="size-4" />
              {t("nuevo")}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute top-2 left-2.5 size-4 text-muted-foreground" />
          <Input className="pl-8" placeholder={t("buscar")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <NativeSelect
          value={clienteId}
          onChange={(e) => {
            setClienteId(e.target.value);
            setPage(0);
          }}
        >
          <option value="">{t("todosClientes")}</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </NativeSelect>
      </div>

      {!data ? (
        <div className="p-8 text-muted-foreground">{tc("cargando")}</div>
      ) : data.items.length === 0 ? (
        <EmptyState message={qDebounced || clienteId ? t("sinResultados") : t("vacio")} />
      ) : (
        <>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("codigo")}</TableHead>
                  <TableHead>{t("nombre")}</TableHead>
                  <TableHead>{t("cliente")}</TableHead>
                  <TableHead>{t("familia")}</TableHead>
                  <TableHead>{t("versiones")}</TableHead>
                  <TableHead>{t("responsable")}</TableHead>
                  <TableHead>{t("actualizado")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer"
                    onClick={() => router.push(`/productos/${p.id}`)}
                  >
                    <TableCell className="font-mono text-xs">{p.codigo ?? "—"}</TableCell>
                    <TableCell>
                      <Link href={`/productos/${p.id}`} className="font-medium hover:underline">
                        {p.nombre}
                      </Link>
                    </TableCell>
                    <TableCell>{p.cliente_nombre ?? "—"}</TableCell>
                    <TableCell>{p.familia_nombre ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={p.versiones_activas ? "emerald" : "neutral"}>{t("activas", { n: p.versiones_activas })}</Badge>
                        {p.borradores > 0 && <Badge tone="amber">{t("borradores", { n: p.borradores })}</Badge>}
                        {p.variantes > 1 && <Badge tone="sky">{t("variantes", { n: p.variantes })}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{p.responsable_nombre ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{fecha(p.actualizado_en)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={size}
            total={data.total}
            onPage={setPage}
            onPageSize={(n) => {
              setSize(n);
              setPage(0);
            }}
          />
        </>
      )}

      <ProductoDialog open={nuevo} onOpenChange={setNuevo} onGuardado={(p) => router.push(`/productos/${p.id}`)} />
    </main>
  );
}
